import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, AlertCircle, CheckCircle2, Loader2, Plus } from 'lucide-react'
import type { ClassWithDetails, RegexGeneration, RegexRule } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { RuleCard } from './RuleCard'
import { RuleFormDrawer } from './RuleFormDrawer'
import { classesApi, classesQueryKey } from '../api'
import { extractApiError } from '@/lib/api'

interface Props {
  detail: ClassWithDetails
}

function formatCost(usd: string | number | null): string {
  if (usd == null) return '—'
  const n = typeof usd === 'string' ? Number.parseFloat(usd) : usd
  if (Number.isNaN(n)) return '—'
  return `$${n.toFixed(4)}`
}

function GenerationMeta({ gen }: { gen: RegexGeneration }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-2xs text-fg-3">
      <span>
        Modelo: <span className="data-mono text-fg-2">{gen.llmModel ?? '—'}</span>
      </span>
      <span>
        Tokens: <span className="data-mono text-fg-2">{gen.llmTokensInput}</span>
        <span> / </span>
        <span className="data-mono text-fg-2">{gen.llmTokensOutput}</span>
      </span>
      <span>
        Custo: <span className="data-mono text-fg-2">{formatCost(gen.llmCostUsd)}</span>
      </span>
      <span>
        Quando: <span className="data-mono text-fg-2">{new Date(gen.createdAt).toLocaleString('pt-BR')}</span>
      </span>
    </div>
  )
}

export function GenerationPanel({ detail }: Props) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [ruleDrawerOpen, setRuleDrawerOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<RegexRule | null>(null)
  const { class: cls, signals, goldExamples, rules, latestGeneration } = detail

  const totalSignals = signals.length
  const canGenerate =
    cls.definition.trim().length >= 10 && goldExamples.length >= 1 && totalSignals >= 1

  const generateMut = useMutation({
    mutationFn: () => classesApi.generate(cls.id),
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: classesQueryKey.detail(cls.id) }),
    onError: (err) => setError(extractApiError(err).message),
  })

  const llmRules = rules.filter((r) => r.generatedBy === 'LLM')
  const coverage = latestGeneration?.coverageReport

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-base text-fg-1 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent-600" />
                Geração via LLM
              </h3>
              <p className="text-sm text-fg-3 mt-0.5">
                Usa definição + sinais + exemplos pra gerar regras de regex e refinar a definição.
              </p>
            </div>
            <Button
              onClick={() => generateMut.mutate()}
              loading={generateMut.isPending}
              disabled={!canGenerate || generateMut.isPending}
            >
              <Sparkles className="h-4 w-4" />
              {llmRules.length > 0 ? 'Regerar' : 'Gerar regex'}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {!canGenerate && (
            <div className="text-sm text-warning bg-warning-bg border border-warning/30 rounded-md px-3 py-2 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Pra gerar regex preciso de: definição (mín. 10 chars), 1+ exemplo-padrão e 1+ sinal
                (trecho ou palavra-chave).
              </span>
            </div>
          )}
          {error && (
            <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {generateMut.isPending && (
            <div className="text-sm text-fg-3 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Pensando… (LLM analisando contexto e validando contra exemplos)
            </div>
          )}
          {latestGeneration && !generateMut.isPending && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                {latestGeneration.status === 'COMPLETED' && (
                  <Badge tone="success">
                    <CheckCircle2 className="h-3 w-3" /> Completado
                  </Badge>
                )}
                {latestGeneration.status === 'FAILED' && <Badge tone="danger">Falhou</Badge>}
                {latestGeneration.status === 'RUNNING' && <Badge tone="warning">Rodando</Badge>}
                {coverage && (
                  <span className="text-sm text-fg-2">
                    <span className="data-mono font-bold">
                      {coverage.rulesCovered}/{coverage.goldTotal}
                    </span>{' '}
                    exemplos cobertos
                  </span>
                )}
              </div>
              <GenerationMeta gen={latestGeneration} />
              {latestGeneration.errorMessage && (
                <p className="text-sm text-danger">{latestGeneration.errorMessage}</p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {latestGeneration?.refinedDefinition && (
        <Card>
          <CardHeader>
            <h3 className="font-display font-bold text-base text-fg-1">Definição refinada</h3>
            <p className="text-sm text-fg-3 mt-0.5">
              Sugestão do LLM com base nos sinais e exemplos. Aplique se for melhor que a sua.
            </p>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-fg-2 leading-relaxed mb-4 whitespace-pre-wrap">
              {latestGeneration.refinedDefinition}
            </p>
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  try {
                    await classesApi.update(cls.id, { definition: latestGeneration.refinedDefinition! })
                    qc.invalidateQueries({ queryKey: classesQueryKey.detail(cls.id) })
                  } catch (err) {
                    setError(extractApiError(err).message)
                  }
                }}
              >
                Aplicar definição refinada
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-display font-bold text-base text-fg-1">
                Regras{' '}
                <span className="text-fg-3 font-normal data-mono text-sm">({rules.length})</span>
              </h3>
              <p className="text-sm text-fg-3 mt-0.5">
                Clique pra expandir. Crie manualmente, edite, ative/desative ou exclua.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditingRule(null)
                setRuleDrawerOpen(true)
              }}
            >
              <Plus className="h-4 w-4" />
              Nova regra manual
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {rules.length === 0 ? (
            <p className="text-sm text-fg-3 text-center py-4">
              Nenhuma regra ainda. Gere via LLM ou crie manualmente.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {rules.map((r) => (
                <RuleCard
                  key={r.id}
                  rule={r}
                  goldTotal={goldExamples.length}
                  onEdit={() => {
                    setEditingRule(r)
                    setRuleDrawerOpen(true)
                  }}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <RuleFormDrawer
        open={ruleDrawerOpen}
        onClose={() => {
          setRuleDrawerOpen(false)
          setEditingRule(null)
        }}
        classId={cls.id}
        goldExamples={goldExamples}
        rule={editingRule}
      />

      {coverage && coverage.uncoveredExampleIndexes.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-display font-bold text-base text-fg-1 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-warning" />
              Exemplos não cobertos
            </h3>
            <p className="text-sm text-fg-3 mt-0.5">
              Nenhuma regra gerada casou com esses exemplos. Considere adicionar trechos típicos deles e regerar.
            </p>
          </CardHeader>
          <CardBody>
            <ul className="flex flex-col gap-2">
              {coverage.uncoveredExampleIndexes.map((idx) => {
                const ex = goldExamples[idx]
                if (!ex) return null
                return (
                  <li
                    key={ex.id}
                    className="rounded-md border border-warning/30 bg-warning-bg/30 px-3 py-2 text-sm text-fg-2"
                  >
                    <span className="data-mono text-2xs text-warning mr-2">#{idx + 1}</span>
                    <span className="line-clamp-2">{ex.text}</span>
                  </li>
                )
              })}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
